/**
 * Helpers for Project Creation, Cloning, and Location derivation.
 */

/**
 * Derives the default folder name from a Git repository URL.
 * Handles HTTPS, SSH, SCP-like syntax, and trailing slashes / .git suffixes.
 */
export function deriveFolderNameFromRepoUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";

  const schemeIndex = trimmed.indexOf("://");
  let pathPart: string;
  if (schemeIndex !== -1) {
    const afterScheme = trimmed.slice(schemeIndex + 3);
    const firstSlash = afterScheme.indexOf("/");
    if (firstSlash === -1) {
      return "";
    }
    pathPart = afterScheme.slice(firstSlash + 1);
  } else {
    const colonIndex = trimmed.indexOf(":");
    if (colonIndex !== -1) {
      pathPart = trimmed.slice(colonIndex + 1);
    } else {
      pathPart = trimmed;
    }
  }

  // Strip trailing slashes
  let clean = pathPart.trim().replace(/\/+$/, "");
  // Strip .git suffix if present (case insensitive)
  if (clean.toLowerCase().endsWith(".git")) {
    clean = clean.slice(0, -4);
  }
  clean = clean.replace(/\/+$/, "");
  const lastSlash = clean.lastIndexOf("/");
  if (lastSlash >= 0) {
    clean = clean.slice(lastSlash + 1);
  }
  clean = clean.trim();
  // Valid entry name check: must not be '.' or '..' and should be reasonable file name
  if (/^[\w.-]+$/.test(clean) && clean !== "." && clean !== "..") {
    return clean;
  }
  return "";
}

/**
 * Validates the optional advanced location (relative parent subdirectory).
 * Rejects traversal (..), leading slashes, drive letters, and backslashes.
 */
export function validateAdvancedSubdir(subdir: string): string | null {
  const trimmed = subdir.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/") || trimmed.startsWith("~") || /^[A-Za-z]:/.test(trimmed)) {
    return "Must be a relative path inside the workspace.";
  }
  if (trimmed.includes("\\")) {
    return "Use '/' as path separator.";
  }
  const segments = trimmed.split("/").filter((s) => s !== "" && s !== ".");
  if (segments.includes("..")) {
    return "Path must not contain '..'.";
  }
  return null;
}

/**
 * Computes the derived display location for a project or destination.
 */
export function computeDerivedLocation(
  workspacePath: string,
  advancedSubdir: string,
  leafName: string,
): string {
  const ws = workspacePath.replace(/\/+$/, "");
  const cleanSub = advancedSubdir.trim().replace(/^\/+/, "").replace(/\/+$/, "");
  const cleanLeaf = leafName.trim();

  const parts = [ws];
  if (cleanSub) parts.push(cleanSub);
  if (cleanLeaf) parts.push(cleanLeaf);
  return parts.join("/");
}
